import styled from 'styled-components';

import type { AdFields } from '@xing-com/crate-ads-native-ads';
import { Ad as NativeAd, SiteSections } from '@xing-com/crate-ads-native-ads';
import type {
  WebsiteAdFieldsFragment,
  VideoAdFieldsFragment,
} from '@xing-com/crate-ads-native-ads/graphql';
import type { AdTypes } from '@xing-com/crate-common-graphql-types';
import { useJobsNwtTracking } from '@xing-com/crate-jobs-tracking';
import { mediaTopBarHidden } from '@xing-com/layout-tokens';
import { spaceL, spaceM, spaceS } from '@xing-com/tokens';

type Props = {
  ad: AdFields;
  position: number;
};

type JobsSearchAd = Extract<
  AdFields,
  WebsiteAdFieldsFragment | VideoAdFieldsFragment
>;

const TrackingAdType: Partial<Record<AdTypes, string>> = {
  VideoAd: 'video',
  WebsiteAd: 'text',
};

const XING_DOMAIN = /https:\/\/www.xing.com/;

export const Ad: React.FC<Props> = ({ ad, position }) => {
  const trackNwt = useJobsNwtTracking();

  // oxlint-disable-next-line typescript/consistent-type-assertions
  const isInternalAd = XING_DOMAIN.test((ad as JobsSearchAd).targetUrl);

  const nwtTrackingOptions = {
    eventSchema: 'searchresult' as const,
    page: 'jobs_search/serp',
    itemUrn: ad.surn,
    position,
    element: 'xing_ad',
    elementDetail: `${TrackingAdType[ad.type]}_ad`,
  };

  const handleOnImpression = (): void => {
    trackNwt({
      ...nwtTrackingOptions,
      event: 'entered_viewport',
    });
  };

  const handleOnClick = (): void => {
    trackNwt({
      ...nwtTrackingOptions,
      event: isInternalAd ? 'opened' : 'visited',
    });
  };

  return (
    <Container>
      <NativeAd
        sectionId={SiteSections.JobsSearch}
        onClickCallback={handleOnClick}
        onImpressionCallback={handleOnImpression}
        {...ad}
      />
    </Container>
  );
};

const Container = styled.div`
  padding: ${spaceM} 0;

  @media ${mediaTopBarHidden} {
    padding: ${spaceL} ${spaceS};
  }
`;
